import { useEffect } from "react";
import type { Locale, RouteKind } from "../content/types";
import { hreflangByLocale, htmlLangByLocale, localizedPaths, locales, ogLocaleByLocale } from "../i18n/routes";

type PageMetaProps = {
  title: string;
  description: string;
  locale: Locale;
  kind: RouteKind;
};

function setMeta(selector: string, attrs: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attrs).forEach(([key, value]) => element?.setAttribute(key, value));
}

function setLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let element = document.head.querySelector<HTMLLinkElement>(selector);
  if (!element) {
    element = document.createElement("link");
    element.rel = rel;
    if (hreflang) element.hreflang = hreflang;
    document.head.appendChild(element);
  }
  element.href = href;
}

export function PageMeta({ title, description, locale, kind }: PageMetaProps) {
  useEffect(() => {
    const origin = "https://prismark.axqora.com";
    const paths = localizedPaths[kind];
    const canonical = `${origin}${paths[locale]}`;

    document.documentElement.lang = htmlLangByLocale[locale];
    document.title = title;
    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('meta[property="og:title"]', { property: "og:title", content: title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: description });
    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    setMeta('meta[property="og:image"]', { property: "og:image", content: `${origin}/images/app-store/Icon.png` });
    setMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    setMeta('meta[property="og:locale"]', { property: "og:locale", content: ogLocaleByLocale[locale] });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setLink("canonical", canonical);
    for (const alt of locales) setLink("alternate", `${origin}${paths[alt]}`, hreflangByLocale[alt]);
    setLink("alternate", `${origin}${paths.en}`, "x-default");
  }, [description, kind, locale, title]);

  return null;
}
